import io from "socket.io-client";
import api, { refreshSession } from "../services/api";

const currentToken = () => api.defaults.headers.Authorization;

class SocketWorker {
  constructor(companyId, userId) {
    const existing = SocketWorker.instance;
    if (existing && existing.companyId === companyId && existing.userId === userId && !existing.disposed) {
      return existing;
    }
    if (existing) existing.disconnect();

    this.companyId = companyId;
    this.userId = userId;
    this.token = currentToken();
    this.socket = null;
    this.eventListeners = {};
    this.disposed = false;
    this.refreshing = null;
    this.lastRefreshAt = 0;
    this.authBlockedToken = null;
    this.lastRefreshedToken = null;
    this.refreshRetryTimer = null;
    this.reconnectTimer = null;
    this.configureSocket();
    SocketWorker.instance = this;
  }

  configureSocket() {
    this.token = currentToken();
    this.socket = io(`${process.env.REACT_APP_BACKEND_URL}/${this.companyId}`, {
      autoConnect: false,
      reconnection: true,
      reconnectionDelay: 1000,
      reconnectionAttempts: Infinity,
      query: { userId: this.userId, token: this.token }
    });

    this.socket.on("connect", () => {
      if (this.reconnectTimer) clearTimeout(this.reconnectTimer);
      this.reconnectTimer = null;
      this.lastRefreshedToken = null;
      console.log("Conectado ao servidor Socket.IO");
    });
    this.socket.on("disconnect", (reason) => {
      console.log("Desconectado do servidor Socket.IO");
      if (reason === "io server disconnect") this.refreshAndReconnect();
      else this.reconnectAfterDelay();
    });
    this.socket.on("connect_error", (error) => {
      if (/expired|invalid token|unauthorized|authentication/i.test(error?.message || "")) {
        this.refreshAndReconnect();
      }
    });

    this.handleResume = () => {
      if (!this.disposed && !document.hidden && navigator.onLine && !this.socket.connected) this.connect();
    };
    window.addEventListener("online", this.handleResume);
    document.addEventListener("visibilitychange", this.handleResume);
    if (!document.hidden && navigator.onLine) this.socket.connect();
  }

  canConnect() {
    return !this.disposed && (typeof document === "undefined" || !document.hidden) &&
      (typeof navigator === "undefined" || navigator.onLine !== false);
  }

  updateToken() {
    this.token = currentToken();
    const queries = [this.socket?.io?.opts?.query, this.socket?.opts?.query, this.socket?.query];
    queries.filter(Boolean).forEach(query => { query.token = this.token; });
  }

  scheduleRefreshRetry() {
    if (this.disposed || this.refreshRetryTimer || !this.canConnect()) return;
    const remaining = Math.max(0, 30000 - (Date.now() - this.lastRefreshAt));
    this.refreshRetryTimer = setTimeout(() => {
      this.refreshRetryTimer = null;
      this.refreshAndReconnect();
    }, remaining);
  }

  async refreshAndReconnect() {
    if (this.disposed || !this.canConnect() || this.authBlockedToken === currentToken()) return;
    // Coalesce simultaneous auth failures and limit refresh attempts to one per 30 seconds.
    if (this.refreshing) return this.refreshing;
    if (this.lastRefreshedToken && this.lastRefreshedToken === currentToken()) {
      this.authBlockedToken = currentToken();
      this.socket.disconnect();
      return;
    }
    if (Date.now() - this.lastRefreshAt < 30000) {
      this.scheduleRefreshRetry();
      return;
    }
    this.lastRefreshAt = Date.now();
    this.refreshing = refreshSession().then((token) => {
      if (!token || this.disposed) return;
      api.defaults.headers.Authorization = `Bearer ${token}`;
      this.authBlockedToken = null;
      this.lastRefreshedToken = currentToken();
      this.updateToken();
      this.connect();
    }).catch((error) => {
      const status = error?.response?.status;
      if (status === 401 || status === 403) {
        this.authBlockedToken = currentToken();
        this.socket.disconnect();
      } else {
        this.scheduleRefreshRetry();
      }
    }).finally(() => { this.refreshing = null; });
    return this.refreshing;
  }

  on(event, callback) {
    this.connect();
    this.socket.on(event, callback);
    if (!this.eventListeners[event]) this.eventListeners[event] = [];
    this.eventListeners[event].push(callback);
  }

  emit(event, data) {
    this.connect();
    this.socket.emit(event, data);
  }

  off(event, callback) {
    if (!this.socket) return;
    if (this.eventListeners[event]) {
      if (callback) {
        this.socket.off(event, callback);
        this.eventListeners[event] = this.eventListeners[event].filter(cb => cb !== callback);
      } else {
        this.eventListeners[event].forEach(cb => this.socket.off(event, cb));
        delete this.eventListeners[event];
      }
    }
  }

  disconnect() {
    if (this.disposed) return;
    this.disposed = true;
    if (this.reconnectTimer) clearTimeout(this.reconnectTimer);
    if (this.refreshRetryTimer) clearTimeout(this.refreshRetryTimer);
    this.reconnectTimer = null;
    this.refreshRetryTimer = null;
    window.removeEventListener("online", this.handleResume);
    document.removeEventListener("visibilitychange", this.handleResume);
    if (this.socket) this.socket.disconnect();
    if (SocketWorker.instance === this) SocketWorker.instance = null;
    console.log("Socket desconectado manualmente");
  }

  reconnectAfterDelay() {
    if (this.disposed || !this.canConnect() || this.authBlockedToken === currentToken() || this.reconnectTimer) return;
    this.reconnectTimer = setTimeout(() => {
      this.reconnectTimer = null;
      if (this.canConnect() && !this.socket.connected) this.connect();
    }, 1000);
  }

  connect() {
    if (!this.socket || this.disposed || !this.canConnect()) return;
    if (this.authBlockedToken && this.authBlockedToken !== currentToken()) this.authBlockedToken = null;
    if (this.authBlockedToken === currentToken()) return;
    this.updateToken();
    if (this.socket.connected || this.socket.active) return;
    this.socket.connect();
  }

  get connected() {
    return this.socket?.connected === true;
  }

  forceReconnect() {
    if (this.disposed || !this.canConnect()) return;
    if (this.socket.connected) this.socket.disconnect();
    this.updateToken();
    this.connect();
  }
}

const instance = (companyId, userId) => (companyId ? new SocketWorker(companyId, userId) : null);

export default instance;
